import React, { useState } from 'react';
import { Check, Info, PhoneForwarded, RefreshCw, WifiOff } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { Note, ScreenHeading, StepScreen, TextAction } from './ActivationPrimitives';
import { MobileActivation } from '../../hooks/useMobileActivation';
interface Props {
  a: MobileActivation;
  onContactSupport?: () => void;
}

/**
 * "Let's try another way" — offers the other method only when it actually fits
 * the selected mode. Progress (completed codes, chosen rules) is kept.
 */
export function ActivationFallbackScreen({
  a,
  onContactSupport
}: Props) {
  const {
    isLight
  } = useTheme();
  const failedCode = a.failedMethod === 'code';
  const retry = () => a.setScreen(failedCode ? 'code' : 'settings');
  const support = () => onContactSupport?.();
  let actions: React.ReactNode;
  if (failedCode && a.iosConditional) {
    actions = <>
        <Button fullWidth onClick={retry} data-id="element-63" data-mp="3csuu5in3pf4c">
          Try activation code again
        </Button>
        <Button fullWidth variant="secondary" onClick={() => a.setScreen('switchConfirm')} data-id="element-64" data-mp="25mt8yzidmc9l">
          Switch to answer every call
        </Button>
        <TextAction onClick={support} data-id="element-65" data-mp="1wnps826om8y9">Contact support</TextAction>
      </>;
  } else if (failedCode) {
    actions = <>
        {a.settingsAvail.available ? <>
            <Button fullWidth onClick={() => a.startMethod('settings')} data-id="element-66" data-mp="2pc3rsmdf5dbx">
              Set up using phone settings
            </Button>
            <Button fullWidth variant="secondary" onClick={retry} data-id="element-67" data-mp="2tcnlffys9ytb">
              Try activation code again
            </Button>
          </> : <Button fullWidth onClick={retry} data-id="element-68" data-mp="2klex6y6g008k">
            Try activation code again
          </Button>}
        <TextAction onClick={support} data-id="element-69" data-mp="38b99nemunmsa">Contact support</TextAction>
      </>;
  } else {
    actions = <>
        {a.codeAvail.available ? <>
            <Button fullWidth onClick={() => a.startMethod('code')} data-id="element-70" data-mp="3d3so0dtd2ly5">
              Try carrier activation code
            </Button>
            <Button fullWidth variant="secondary" onClick={retry} data-id="element-71" data-mp="3hyv25qhk9jo2">
              Review settings instructions
            </Button>
          </> : <Button fullWidth onClick={retry} data-id="element-72" data-mp="3cxntzqbiaigv">
            Review settings instructions
          </Button>}
        <TextAction onClick={support} data-id="element-73" data-mp="382rzmjisrj0l">Contact support</TextAction>
      </>;
  }
  const doneSteps = failedCode ? a.codeStepIdx : 0;
  return <StepScreen stepKey="fallback" onBack={retry} footer={actions} data-id="element-74" data-mp="2z7u0uvsyxc1c">
      <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-75" data-mp="2u90qhc8za15e">
        <RefreshCw className="h-6 w-6" data-id="element-76" data-mp="2pbh8c195t486" />
      </div>
      <ScreenHeading title="Let's try another way" subtitle="We couldn't verify your call forwarding setup. Let's try another method." data-id="element-77" data-mp="2kbcsmdg2sdj0" />

      {doneSteps > 0 && <Note icon={Check} data-id="element-78" data-mp="vrh2fg4q93f1">
          Your progress is saved — {doneSteps} of {a.steps.length} codes already done. You'll pick up where you left off.
        </Note>}

      {failedCode && a.iosConditional && <Note icon={Info} data-id="element-79" data-mp="14rdhqr588pxf">
          iPhone Settings normally forwards <b data-id="element-80" data-mp="14q2hv2ewk2bl">every</b> call, so it can't replace “when I can't answer”. You can retry
          the code, get help from us or your carrier, or choose to let Vexu answer every call.
        </Note>}

      {failedCode && a.carrier.wifiCallingTip && <Note icon={WifiOff} tone="amber" data-id="element-81" data-mp="h3ce9wmwnzov">
          {a.carrier.wifiCallingTip}
        </Note>}

      {!failedCode && a.os === 'ios' && <Note icon={Info} data-id="element-82" data-mp="lzkqb15t3crt">
          If Call Forwarding is missing from iPhone Settings, your carrier may only allow it using a code.
        </Note>}
    </StepScreen>;
}

/** Explicit confirmation before changing "when I can't answer" to "every call". */
export function SwitchModeConfirmScreen({
  a,
  onCancel
}: {
  a: MobileActivation;
  onCancel: () => void;
}) {
  const {
    isLight
  } = useTheme();
  const [understood, setUnderstood] = useState(false);
  return <StepScreen stepKey="switchConfirm" onBack={onCancel} footer={<>
          <Button fullWidth disabled={!understood} onClick={a.switchToAllCalls} data-id="element-84" data-mp="29sd40ijp8ddl">
            Yes, answer every call
          </Button>
          <Button fullWidth variant="secondary" onClick={onCancel} data-id="element-85" data-mp="2joybdaj6y0kl">
            Keep “when I can't answer”
          </Button>
        </>} data-id="element-83" data-mp="1w0ymyh9cqm96">
      <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-86" data-mp="2ep3jb3noj4xy">
        <PhoneForwarded className="h-6 w-6" data-id="element-87" data-mp="2nqjtpbehv726" />
      </div>
      <ScreenHeading title="Let Vexu answer every call?" subtitle="Incoming calls will go straight to your AI assistant instead of ringing your phone first." data-id="element-88" data-mp="2nr32o2m62l42" />
      <button type="button" role="checkbox" aria-checked={understood} onClick={() => setUnderstood(v => !v)} className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left ${isLight ? 'border-slate-200 bg-white' : 'border-white/10 bg-white/5'}`} data-id="element-89" data-mp="tdlm4zk6ioij">
        <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${understood ? 'border-vexu bg-vexu' : isLight ? 'border-slate-300' : 'border-white/25'}`} data-id="element-90" data-mp="2w6snb9uf8v19">
          {understood && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} data-id="element-91" data-mp="2mbh6mlzgddqv" />}
        </span>
        <span className={`text-[13px] leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-92" data-mp="2r9sewsa3ng6z">
          I understand my phone won't ring for forwarded calls. I can switch back anytime.
        </span>
      </button>
    </StepScreen>;
}